import { useEffect, useRef } from "react";
import clsx from "clsx";
import _ from "lodash";
import { useLocation } from "react-router";
import { Link as RouterLink } from "react-router-dom";

import { ChevronsRight, ChevronsLeft } from "lucide-react";

import FreshpaintLogoLight from "@/components/FpLogoLight";
import FreshpaintLogoSmall from "@/components/FpLogoSmall";
import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import { LocalStorageProject } from "@/reducers/environments";
import NavBarSkeleton from "./NavBarSkeleton";
import { NavigationEnvironmentPicker } from "./EnvironmentPicker";
import usePermissions from "@/access-control/queries/usePermissions";
import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import { useAccountEntitlements } from "@/entitlements/queries/useAccountEntitlements";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import useFeatureFlags from "@/hooks/useFeatureFlag";
import useUsers from "@/hooks/useUsers";
import useCurrentProject from "@/hooks/use-project";
import useCanViewDestinationRanks from "@/admin/useCanViewDestinationRanks";
import useWTMURLs from "@/trackers/useWTMURLs";
import FreshpaintUser from "@/components/users/FreshpaintUser";
import { NavBarProps } from "./types";
import { renderNavItems } from "./renderingUtils";
import {
  createScrollHandler,
  resetScrollPosition,
  restoreScrollPosition,
} from "./scrollUtils";
import {
  getSystemStatusNavItems,
  getFreshpaintAINavItems,
  getInsightsNavItems,
  getAnalyticsNavItems,
  getAudienceNavItems,
  getEventNavItems,
  getIntegrationNavItemsV1,
  getIntegrationNavItemsV2,
  getInternalToolsNavItems,
  getProfilesNavItems,
  getSettingsNavItems,
  getSetupNavItems,
  getVideoNavItems,
  getWebTrackerMonitoringNavItems,
  getWTMAdminNavItems,
  getConsentManagementNavItems,
} from "./navigationItems";
import useCanAccessAiChat from "@/hooks/useCanAccessAiChat";
import useCanAccessCRO from "@/hooks/useCanAccessCRO";
import { useUserHasAccess } from "@/hooks/useUserHasAccess";
import { useUserHasAccessToAny } from "@/hooks/useUserHasAccessToAny";

const NavBar = ({
  className,
  drawerCollapse,
  setDrawerCollapse,
  ...rest
}: NavBarProps) => {
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const accountMetadata = useAccountMetadata();
  const usersState = useUsers();
  const { isLoading: permsLoading } = usePermissions();
  const { isLoading: entitlementsLoading } = useAccountEntitlements();

  useEffect(() => {
    const eventHandler = createScrollHandler(scrollContainerRef);

    if (scrollContainerRef.current) {
      scrollContainerRef.current.addEventListener("scroll", eventHandler);
    }

    const scrollContainer = scrollContainerRef.current;
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", eventHandler);
      }
    };
  }, []);

  useEffect(() => {
    window.addEventListener("beforeunload", resetScrollPosition);
    return () =>
      window.removeEventListener("beforeunload", resetScrollPosition);
  }, []);

  useEffect(() => {
    restoreScrollPosition(scrollContainerRef);
  }, []);

  const accountMetadataLoaded = accountMetadata.loaded ?? false;

  const {
    systemStatusNavItems,
    freshpaintAINavItems,
    setupNavItems,
    eventNavItems,
    integrationNavItems,
    insightsNavItems,
    analyticsNavItems,
    audienceNavItems,
    profilesNavItems,
    consentManagementNavItems,
    videoNavItems,
    webTrackerMonitoringNavItems,
    internalToolsNavItems,
    wtmAdminNavItems,
    settingsNavItems,
  } = useNavItems();

  if (usersState.loading || permsLoading || entitlementsLoading) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="dark:bg-nav-background flex h-[68px] items-center justify-between bg-gray-700 px-4 transition-all duration-300">
        <RouterLink to="/" className="relative flex h-9 items-center">
          <span
            className={clsx(
              "absolute transition-opacity duration-300",
              drawerCollapse ? "opacity-100" : "opacity-0",
            )}
          >
            <FreshpaintLogoSmall height="36" />
          </span>
          <span
            className={clsx(
              "transition-opacity duration-300",
              drawerCollapse ? "opacity-0" : "opacity-100",
            )}
          >
            <FreshpaintLogoLight height="36" />
          </span>
        </RouterLink>
        <Tooltip
          content={drawerCollapse ? "Expand navigation" : "Collapse navigation"}
        >
          <button
            type="button"
            onClick={() => setDrawerCollapse(!drawerCollapse)}
            aria-label={
              drawerCollapse ? "Expand navigation" : "Collapse navigation"
            }
            className={clsx(
              "hover:bg-nav-hover flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-white transition-all duration-300",
              drawerCollapse && "w-0 overflow-hidden opacity-0",
            )}
          >
            {drawerCollapse ? (
              <ChevronsRight size={18} />
            ) : (
              <ChevronsLeft size={18} />
            )}
          </button>
        </Tooltip>
      </div>
      {drawerCollapse && (
        <div className="dark:bg-nav-background flex justify-center bg-gray-700 px-1.5 py-3">
          <Tooltip content="Expand navigation">
            <button
              type="button"
              onClick={() => setDrawerCollapse(false)}
              aria-label="Expand navigation"
              className="hover:bg-nav-hover flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-white transition-colors"
            >
              <ChevronsRight size={18} />
            </button>
          </Tooltip>
        </div>
      )}
      <NavigationEnvironmentPicker collapsed={drawerCollapse} />
      <div className="dark:bg-nav-background border-b border-white/15 bg-gray-700" />
      <nav
        {...rest}
        className={clsx(
          "scrollbar dark:bg-nav-background flex flex-1 flex-col overflow-y-auto bg-gray-700 p-1.5 pb-1",
          className,
        )}
        ref={scrollContainerRef}
      >
        {accountMetadataLoaded ? (
          <>
            {renderNavItems({
              collapsed: drawerCollapse,
              items: freshpaintAINavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: setupNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: eventNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: integrationNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: analyticsNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: insightsNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: audienceNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: profilesNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: consentManagementNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: videoNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: webTrackerMonitoringNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: internalToolsNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: wtmAdminNavItems,
              pathname: location.pathname,
            })}
          </>
        ) : (
          <NavBarSkeleton />
        )}
      </nav>
      {accountMetadataLoaded && (
        <div className="dark:bg-nav-background flex flex-col border-t border-white/15 bg-gray-700 p-1.5">
          {renderNavItems({
            collapsed: drawerCollapse,
            items: systemStatusNavItems,
            pathname: location.pathname,
          })}
          {renderNavItems({
            collapsed: drawerCollapse,
            items: settingsNavItems,
            pathname: location.pathname,
          })}
        </div>
      )}
    </div>
  );
};

export function useNavItems() {
  const accountMetadata = useAccountMetadata();
  const flag = useFeatureFlags();
  const wtmURLs = useWTMURLs();
  const usersState = useUsers();
  const { data: rolePermissions } = usePermissions();
  const { data: accountEntitlements } = useAccountEntitlements();

  const currentProject = useCurrentProject();
  const hasGlobalAllowListEntries = (() => {
    if (!currentProject.loaded) {
      return false;
    }
    const p = currentProject as LocalStorageProject;
    return (
      !_.isEmpty(p.eventPropertySensitivityLevels) ||
      !_.isEmpty(p.userPropertySensitivityLevels) ||
      !_.isEmpty(p.groupPropertySensitivityLevels)
    );
  })();

  const canViewDestinationRanks = useCanViewDestinationRanks();
  const canEditEvents = useUserHasAccess(
    RBACDomain.eventLibrary,
    RBACAccessLevel.write,
  );

  const canEditDestinations = useUserHasAccess(
    RBACDomain.destinationConfiguration,
    RBACAccessLevel.write,
  );
  const canAccessVideo = useUserHasAccess(
    RBACDomain.video,
    RBACAccessLevel.write,
  );

  const canAccessAudiences = useUserHasAccessToAny(
    RBACDomain.audiences,
    RBACAccessLevel.read,
    RBACAccessLevel.write,
  );

  const canAccessOfflineAttributions = useUserHasAccess(
    RBACDomain.offlineAttributions,
    RBACAccessLevel.read,
  );
  const canAccessWtm = useUserHasAccess(
    RBACDomain.webTrackerMonitoring,
    RBACAccessLevel.write,
  );
  const canAccessUserProfiles = useUserHasAccess(
    RBACDomain.userProfiles,
    RBACAccessLevel.read,
  );

  const canAccessCRO = useCanAccessCRO();
  const canAccessAiChat = useCanAccessAiChat();

  const hasAdminAccess =
    !usersState.loading &&
    new FreshpaintUser(usersState.currentUser).hasAdminAccess();

  const systemStatusNavItems = getSystemStatusNavItems(hasAdminAccess);
  const freshpaintAINavItems = getFreshpaintAINavItems(canAccessAiChat);
  const setupNavItems = getSetupNavItems();
  const eventNavItems = getEventNavItems(
    accountMetadata,
    !!canEditEvents,
    hasGlobalAllowListEntries,
  );
  const integrationNavItemsV1 = getIntegrationNavItemsV1();
  const integrationNavItemsV2 = getIntegrationNavItemsV2(
    accountMetadata,
    flag,
    !!canEditDestinations,
    !!canAccessAudiences,
    accountEntitlements,
  );
  const integrationNavItems = flag("destinationsV2")
    ? integrationNavItemsV2
    : integrationNavItemsV1;
  const analyticsNavItems = getAnalyticsNavItems(flag);
  const insightsNavItems = getInsightsNavItems(
    flag,
    !!canAccessOfflineAttributions,
    canAccessCRO,
  );
  const audienceNavItems = canAccessAudiences
    ? getAudienceNavItems(flag, !!canAccessAudiences)
    : [];
  const profilesNavItems = getProfilesNavItems(flag, !!canAccessUserProfiles);
  const consentManagementNavItems = getConsentManagementNavItems(flag);
  const videoNavItems = getVideoNavItems(accountEntitlements, !!canAccessVideo);
  const webTrackerMonitoringNavItems = getWebTrackerMonitoringNavItems(
    wtmURLs,
    flag,
    accountMetadata,
    !!canAccessWtm,
  );
  const internalToolsNavItems = getInternalToolsNavItems(
    canViewDestinationRanks,
    flag,
  );
  const wtmAdminNavItems = getWTMAdminNavItems();
  const settingsNavItems = getSettingsNavItems(
    flag,
    usersState,
    rolePermissions,
  );

  return {
    systemStatusNavItems,
    freshpaintAINavItems,
    setupNavItems,
    eventNavItems,
    integrationNavItems,
    insightsNavItems,
    analyticsNavItems,
    audienceNavItems,
    profilesNavItems,
    consentManagementNavItems,
    videoNavItems,
    webTrackerMonitoringNavItems,
    internalToolsNavItems,
    wtmAdminNavItems,
    settingsNavItems,
  };
}

export default NavBar;
